import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import Plot from "react-plotly.js";
import { Play, History, Loader2, Palette, Download, MapPin } from "lucide-react";
import GIF from "gif.js";
import { PALETTES, DEFAULT_PALETTE, stopsToGradient } from "../components/InSpektViewer";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtEpoch(key) {
  return key ? key.replace(/^D_(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3") : key;
}
function daysBetween(a, b) {
  const parse = k => { const m = k?.match(/^D_(\d{4})(\d{2})(\d{2})$/); return m ? new Date(+m[1], +m[2]-1, +m[3]) : null; };
  const da = parse(a), db = parse(b);
  return (da && db) ? Math.round((db - da) / 86400000) : 0;
}
function fmtDelta(days) {
  if (days <= 0) return null;
  const years = Math.floor(days / 365), months = Math.floor((days % 365) / 30);
  let l = "";
  if (years > 0) l += `${years}y `;
  if (months > 0) l += `${months}m `;
  return `${(l.trim() || "<1m")} (${days}d)`;
}
function hexToPlotly(stops) {
  return stops.map((hex, i) => {
    const n = parseInt(hex.replace("#", ""), 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    return [i / (stops.length - 1), `rgb(${r},${g},${b})`];
  });
}

// ── Clean 3D axis (no ticks/labels/grid — hover still works) ─────────────────
const CLEAN_AXIS = {
  showticklabels: false,
  showgrid:       false,
  zeroline:       false,
  showline:       false,
  showspikes:     false,
  title:          { text: "" },
};

// Loop-based min/max — Math.min(...arr) spreads the array into arguments and
// throws "Maximum call stack size exceeded" beyond ~120k elements, which large
// selection areas easily produce.
function arrMinMax(arr) {
  let mn = Infinity, mx = -Infinity;
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i];
    if (v < mn) mn = v;
    if (v > mx) mx = v;
  }
  return [mn, mx];
}

// ── IDW interpolation modes ───────────────────────────────────────────────────
const IDW_MODES = [
  { label: "Linear",    power: 1, title: "Linear (p=1) — uniform regional influence, smoothest surface" },
  { label: "Quadratic", power: 2, title: "Quadratic (p=2) — balanced distance weighting (default)" },
  { label: "Cubic",     power: 3, title: "Cubic (p=3) — emphasises nearby measurements, sharper gradients" },
  { label: "Shepard",   power: 5, title: "Shepard (p=5) — near-exact at measurement points, very sharp" },
];

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  text:   "#232231",
  muted:  "#6b6a8f",
  subtle: "#9898b8",
  bg:     "#f6f6fb",
  border: "#e3e2f0",
  rSm:    6,
  accent: "#232231",
};

// ── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ children, compact }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.07em",
      textTransform: "uppercase", color: T.muted,
      marginBottom: compact ? 6 : 8,
      marginTop:    compact ? 0 : 16,
    }}>
      {children}
    </div>
  );
}

// ── Section block with top divider ────────────────────────────────────────────
function SectionBlock({ children }) {
  return (
    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, marginTop: 14 }}>
      {children}
    </div>
  );
}

// ── Colour-style panel (draggable floating card) ──────────────────────────────
function ColourStylePanel({ currentStops, lockToMap, onApply, onClose, initialPos }) {
  const [localStops, setLocalStops] = useState([...currentStops]);
  const [localLock,  setLocalLock]  = useState(lockToMap);
  const [pos,        setPos]        = useState(initialPos || { x: 340, y: 140 });
  const dragOrigin = useRef(null);

  const activePreset = PALETTES.find(p => JSON.stringify(p.stops) === JSON.stringify(localStops));
  const STOP_LABELS  = ["Neg max", "Neg mid", "Zero", "Pos mid", "Pos max"];

  const onHeaderMouseDown = (e) => {
    if (e.target.closest("button") || e.target.closest("input")) return;
    dragOrigin.current = { startX: e.clientX - pos.x, startY: e.clientY - pos.y };
    const onMove = (ev) => setPos({ x: ev.clientX - dragOrigin.current.startX, y: ev.clientY - dragOrigin.current.startY });
    const onUp   = () => { dragOrigin.current = null; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div style={{
      position: "fixed", zIndex: 10000,
      left: pos.x, top: pos.y,
      width: 290,
      background: "#fff",
      borderRadius: 8,
      boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 14px", borderBottom: `1px solid ${T.border}`,
        background: T.bg, cursor: "grab", userSelect: "none",
      }} onMouseDown={onHeaderMouseDown}>
        <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>Surface Colour Style</span>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, padding: 2, display: "flex" }}>
          ✕
        </button>
      </div>

      <div style={{ padding: "12px 14px" }}>
        {/* Presets */}
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.muted, marginBottom: 8 }}>
          Presets
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 }}>
          {PALETTES.map(p => {
            const isActive = activePreset?.id === p.id;
            return (
              <div key={p.id} onClick={() => setLocalStops([...p.stops])} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "5px 8px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${isActive ? "#1a73e8" : T.border}`,
                background: isActive ? "#e8f0fe" : "#fff",
                transition: "all 0.15s",
              }}>
                <div style={{ flex: 1, height: 10, borderRadius: 3, background: stopsToGradient(p.stops) }} />
                <span style={{ fontSize: 11, color: isActive ? "#1a73e8" : "#374151", fontWeight: isActive ? 600 : 400, whiteSpace: "nowrap", minWidth: 90 }}>
                  {p.name}
                </span>
                {isActive && <span style={{ color: "#1a73e8", fontSize: 11, fontWeight: 700 }}>✓</span>}
              </div>
            );
          })}
        </div>

        {/* Custom colour pickers */}
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.muted, marginBottom: 8 }}>
          Custom
        </div>
        <div style={{ height: 10, borderRadius: 3, background: stopsToGradient(localStops), marginBottom: 10 }} />
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          {localStops.map((hex, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <input
                type="color" value={hex}
                onChange={e => { const s = [...localStops]; s[i] = e.target.value; setLocalStops(s); }}
                style={{ width: 32, height: 28, border: `1px solid ${T.border}`, borderRadius: 4, cursor: "pointer", padding: 1 }}
              />
              <span style={{ fontSize: 9, color: T.muted, textAlign: "center", lineHeight: 1.2 }}>{STOP_LABELS[i]}</span>
            </div>
          ))}
        </div>

        {/* Lock to map */}
        <div
          onClick={() => setLocalLock(v => !v)}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "7px 10px", borderRadius: 6, cursor: "pointer",
            border: `1px solid ${localLock ? "#1a73e8" : T.border}`,
            background: localLock ? "#e8f0fe" : T.bg,
            marginBottom: 12, userSelect: "none", transition: "all 0.15s",
          }}
        >
          <div style={{
            width: 14, height: 14, borderRadius: 3, flexShrink: 0,
            border: `2px solid ${localLock ? "#1a73e8" : "#d1d5db"}`,
            background: localLock ? "#1a73e8" : "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.12s, border-color 0.12s",
          }}>
            {localLock && <span style={{ color: "#fff", fontSize: 9, lineHeight: 1 }}>✓</span>}
          </div>
          <span style={{ fontSize: 11, color: localLock ? "#1a73e8" : T.text, fontWeight: localLock ? 600 : 400 }}>
            Lock colour scale to map
          </span>
        </div>

        {/* Apply */}
        <button
          onClick={() => onApply(localStops, localLock)}
          style={{
            width: "100%", padding: "6px 0", background: T.text, color: "#fff",
            border: "none", borderRadius: T.rSm, fontSize: 11, fontWeight: 600,
            cursor: "pointer", transition: "background 0.12s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "#374151"}
          onMouseLeave={e => e.currentTarget.style.background = T.text}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

export default function Surface3DChart({
  surfaceData,
  polygonDrawn,
  rangeMin = null,
  rangeMax = null,
  variant = "default",         // "default" = Map.js  |  "cesium" = InSpektViewer
  epochs = [],
  selectedEpoch = null,
  onEpochChange,
  epochsLoading = false,
  onLoadEpochs,
  onCloseEpochs,
  colorStops = null,           // hex stops from layer legend — used when lockToMap is true
  mapEpoch = null,             // non-null when map epoch differs from surface display epoch
  onResetEpoch,                // callback to reset map to latest epoch
  onApplyColorStops,           // callback(stops) — notify InSpektViewer to sync map colour
  onAxisModeChange,            // callback(mode) — notify InSpektViewer when axis mode changes
  onIdwPowerChange,            // callback(power) — notify InSpektViewer when IDW mode changes
  onOpacityChange,             // callback(opacity) — notify InSpektViewer when surface opacity changes
  onDiffChange,                // callback(epochA|null, epochB|null) — notify InSpektViewer of diff mode
  onPointsOpacityChange,       // callback(opacity) — notify InSpektViewer when PS point opacity changes
  onExportGif,                 // async callback(opts) → blob — trigger Cesium canvas GIF capture
  onExportDiffPng,             // async callback(opts) — trigger Cesium canvas diff PNG capture
  hideHistory = false,
}) {
  const isCesium  = variant === "cesium";
  const hasEpochs = isCesium && epochs.length > 1;

  // ── UI state ──────────────────────────────────────────────────────────────────
  const [zScale,         setZScale]         = useState(10);
  const [axisMode,       setAxisMode]       = useState("distance");
  const handleAxisMode = (mode) => { setAxisMode(mode); onAxisModeChange?.(mode); };
  const [isPlaying,      setIsPlaying]      = useState(false);
  const [intervalInput,  setIntervalInput]  = useState("2");
  const [showColourPanel,  setShowColourPanel]  = useState(false);
  const [showExportPanel,  setShowExportPanel]  = useState(false);
  const [showDataPoints,   setShowDataPoints]   = useState(false);
  const [colourPanelPos,   setColourPanelPos]   = useState(null);
  const colourBtnRef = useRef(null);
  const cardRef      = useRef(null);
  // Surface-local colour state: stops + whether it's locked to map
  const [localStops,     setLocalStops]     = useState(null);   // null = follow colorStops prop
  const [lockToMap,      setLockToMap]      = useState(true);   // default: follow map palette
  const [isExporting,    setIsExporting]    = useState(false);
  const [exportFilename, setExportFilename] = useState("surface-deformation");
  const [exportTitle,    setExportTitle]    = useState("");
  const [exportSpeed,    setExportSpeed]    = useState(300); // ms per frame
  const plotDivRef = useRef(null); // DOM element captured from Plot onInitialized

  const [idwModeIdx,    setIdwModeIdx]    = useState(1); // 0=Linear 1=Quadratic 2=Cubic 3=Shepard
  const idwPower = IDW_MODES[idwModeIdx].power;
  const [showColourbar, setShowColourbar] = useState(false);
  const [histMode,      setHistMode]      = useState("slider"); // "slider" | "diff"
  const diffMode = histMode === "diff";
  const [diffEpochA,    setDiffEpochA]    = useState(null);
  const [diffEpochB,    setDiffEpochB]    = useState(null);
  const [diffGridZ,          setDiffGridZ]          = useState(null);
  const [showDiffExportPanel, setShowDiffExportPanel] = useState(false);
  const [diffExportFilename,  setDiffExportFilename]  = useState("surface-diff");
  const [diffExportTitle,     setDiffExportTitle]     = useState("");
  const [diffExportShowStats, setDiffExportShowStats] = useState(true);
  const [diffExportDpi,       setDiffExportDpi]       = useState(300);
  const [isDiffExporting,     setIsDiffExporting]     = useState(false);
  const [gifExportDpi,        setGifExportDpi]        = useState(300);
  const [gifExportProgress,   setGifExportProgress]   = useState(null); // null=idle, 0-100=building
  const [gifBlobReady,        setGifBlobReady]        = useState(null); // {blob,fname} when encoded
  const [opacity,             setOpacity]             = useState(0.82);
  const [pointsOpacity,       setPointsOpacity]       = useState(1.0);

  const [camera, setCamera] = useState({
    eye:    { x: -1, y: -1, z:  1 },
    up:     { x:  0, y:  0, z:  1 },
    center: { x:  0, y:  0, z:  0 },
    fov: 40,
  });

  const selectedEpochRef = useRef(selectedEpoch);
  useEffect(() => { selectedEpochRef.current = selectedEpoch; }, [selectedEpoch]);

  // When locked, keep local stops in sync with incoming colorStops prop
  useEffect(() => {
    if (lockToMap && colorStops) setLocalStops(colorStops);
  }, [lockToMap, colorStops]);

  // Reset local stops when new polygon drawn (epochs reset)
  useEffect(() => {
    setIsPlaying(false);
    setLocalStops(null);
    setLockToMap(true);
    setHistMode("slider");
    setDiffEpochA(null);
    setDiffEpochB(null);
    setDiffGridZ(null);
    setIdwModeIdx(1);
    setOpacity(0.82);
    setPointsOpacity(1.0);
    setGifBlobReady(null);
  }, [epochs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify InSpektViewer when diff mode / epoch selection changes (cesium overlay)
  useEffect(() => {
    if (!isCesium) return;
    if (diffMode && diffEpochA && diffEpochB) {
      onDiffChange?.(diffEpochA, diffEpochB);
    } else {
      onDiffChange?.(null, null);
    }
  }, [diffMode, diffEpochA, diffEpochB, isCesium]); // eslint-disable-line react-hooks/exhaustive-deps

  // ESC → stop playback
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setIsPlaying(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Play loop — advances one epoch every 600 ms
  useEffect(() => {
    if (!isPlaying || !hasEpochs) return;
    const id = setInterval(() => {
      const cur = epochs.indexOf(selectedEpochRef.current);
      if (cur < epochs.length - 1) {
        const next = epochs[cur + 1];
        selectedEpochRef.current = next;
        onEpochChange?.(next);
      } else {
        setIsPlaying(false);
      }
    }, 600);
    return () => clearInterval(id);
  }, [isPlaying, hasEpochs, epochs, onEpochChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlay = () => {
    if (!hasEpochs) return;
    selectedEpochRef.current = epochs[0];
    onEpochChange?.(epochs[0]);
    setIsPlaying(true);
  };

  // Effective stops: local override > map stops > default palette
  const effectiveStops = localStops || colorStops || DEFAULT_PALETTE.stops;

  // ── 1) extract coords + stats ─────────────────────────────────────────────────
  const { x, y, z, min, max, avg } = useMemo(() => {
    if (!surfaceData?.length) return { x: [], y: [], z: [], min: 0, max: 0, avg: 0 };

    const points = surfaceData.map(p => {
      let v;
      if (isCesium && selectedEpoch && p.epochs) {
        v = p.epochs[selectedEpoch];
      } else {
        v = p.value;
      }
      return (v != null && isFinite(v)) ? { lat: p.lat, lon: p.lon, v } : null;
    }).filter(Boolean);

    if (!points.length) return { x: [], y: [], z: [], min: 0, max: 0, avg: 0 };

    const lons = points.map(p => p.lon);
    const lats = points.map(p => p.lat);
    const vals = points.map(p => p.v);

    const [min, max] = arrMinMax(vals);
    const avg = vals.reduce((sum, v) => sum + v, 0) / vals.length;

    if (axisMode === "coordinates") {
      return { x: lons, y: lats, z: vals, min, max, avg };
    }

    const lon0 = lons[0], lat0 = lats[0];
    const toRad = d => (d * Math.PI) / 180;
    const φ0 = toRad(lat0);
    const mPerLon = 111320 * Math.cos(φ0);
    const mPerLat = 110540;
    const xs = lons.map(lon => (lon - lon0) * mPerLon);
    const ys = lats.map(lat => (lat - lat0) * mPerLat);

    return { x: xs, y: ys, z: vals, min, max, avg };
  }, [surfaceData, axisMode, isCesium, selectedEpoch]);

  // ── 2) IDW interpolation helper ───────────────────────────────────────────────
  const idwPredict = useCallback((px, py, xs, ys, zs, power = 2) => {
    let num = 0, den = 0;
    for (let i = 0; i < xs.length; i++) {
      const dx = px - xs[i];
      const dy = py - ys[i];
      const d2 = dx * dx + dy * dy;
      if (d2 === 0) return zs[i];
      const w = 1 / Math.pow(d2, power / 2);
      num += w * zs[i];
      den += w;
    }
    return den === 0 ? 0 : num / den;
  }, []);

  // ── 3) adaptive rectangular grid ─────────────────────────────────────────────
  const { xGrid, yGrid, nx, ny } = useMemo(() => {
    const MAX_RES = 50;
    if (!x.length) return { xGrid: [], yGrid: [], nx: 0, ny: 0 };

    const [xMin, xMax] = arrMinMax(x);
    const [yMin, yMax] = arrMinMax(y);

    const xPad = (xMax - xMin) * 0.05 || 1;
    const yPad = (yMax - yMin) * 0.05 || 1;
    const xMinP = xMin - xPad, xMaxP = xMax + xPad;
    const yMinP = yMin - yPad, yMaxP = yMax + yPad;

    const dx = xMaxP - xMinP;
    const dy = yMaxP - yMinP;
    const ratio = dx / dy || 1;
    let nx = Math.max(10, Math.floor(MAX_RES * Math.sqrt(ratio)));
    let ny = Math.max(10, Math.floor(MAX_RES / Math.sqrt(ratio)));

    const xGrid = Array.from({ length: nx }, (_, i) => xMinP + dx * (i / (nx - 1)));
    const yGrid = Array.from({ length: ny }, (_, j) => yMinP + dy * (j / (ny - 1)));
    return { xGrid, yGrid, nx, ny };
  }, [x, y]);

  // ── 4) predict onto grid ──────────────────────────────────────────────────────
  const [gridZ, setGridZ] = useState(null);
  useEffect(() => {
    // The cesium variant never renders the Plotly chart (the globe overlay is
    // the surface); skip the grid IDW entirely — on large selections it costs
    // seconds of main-thread time for output nothing displays.
    if (isCesium) { setGridZ(null); return; }
    if (!x.length || x.length < 4) { setGridZ(null); return; }
    const zFlat = new Array(nx * ny);
    let idx = 0;
    for (let j = 0; j < ny; j++) {
      const yv = yGrid[j];
      for (let i = 0; i < nx; i++, idx++) {
        zFlat[idx] = Math.min(max, Math.max(min, idwPredict(xGrid[i], yv, x, y, z, idwPower)));
      }
    }
    const z2d = [];
    for (let j = 0; j < ny; j++) z2d.push(zFlat.slice(j * nx, (j + 1) * nx));
    setGridZ(z2d);
  }, [x, y, z, xGrid, yGrid, nx, ny, min, max, idwPredict, idwPower, isCesium]);

  // ── 5) diff data points ───────────────────────────────────────────────────────
  const { diffX, diffY, diffZ, diffMin, diffMax, diffAvg } = useMemo(() => {
    const empty = { diffX: [], diffY: [], diffZ: [], diffMin: 0, diffMax: 0, diffAvg: 0 };
    if (!diffMode || !diffEpochA || !diffEpochB || !surfaceData?.length) return empty;
    const pts = surfaceData.map(p => {
      const va = p.epochs?.[diffEpochA];
      const vb = p.epochs?.[diffEpochB];
      if (va == null || vb == null || !isFinite(va) || !isFinite(vb)) return null;
      return { lat: p.lat, lon: p.lon, dz: vb - va };
    }).filter(Boolean);
    if (!pts.length) return empty;
    const lons = pts.map(p => p.lon);
    const lats = pts.map(p => p.lat);
    const dzs  = pts.map(p => p.dz);
    const [dMin, dMax] = arrMinMax(dzs);
    const dAvg = dzs.reduce((s, v) => s + v, 0) / dzs.length;
    if (axisMode === "coordinates") {
      return { diffX: lons, diffY: lats, diffZ: dzs, diffMin: dMin, diffMax: dMax, diffAvg: dAvg };
    }
    const lon0 = lons[0], lat0 = lats[0];
    const φ0 = (lat0 * Math.PI) / 180;
    const mPerLon = 111320 * Math.cos(φ0);
    const mPerLat = 110540;
    return {
      diffX: lons.map(lon => (lon - lon0) * mPerLon),
      diffY: lats.map(lat => (lat - lat0) * mPerLat),
      diffZ: dzs, diffMin: dMin, diffMax: dMax, diffAvg: dAvg,
    };
  }, [diffMode, diffEpochA, diffEpochB, surfaceData, axisMode]);

  // ── 6) IDW onto diff grid ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!diffMode || diffX.length < 4 || !xGrid.length) { setDiffGridZ(null); return; }
    const zFlat = new Array(nx * ny);
    let idx = 0;
    for (let j = 0; j < ny; j++) {
      const yv = yGrid[j];
      for (let i = 0; i < nx; i++, idx++) {
        zFlat[idx] = idwPredict(xGrid[i], yv, diffX, diffY, diffZ, idwPower);
      }
    }
    const z2d = [];
    for (let j = 0; j < ny; j++) z2d.push(zFlat.slice(j * nx, (j + 1) * nx));
    setDiffGridZ(z2d);
  }, [diffMode, diffX, diffY, diffZ, xGrid, yGrid, nx, ny, idwPower, idwPredict]);

  // ── Early exits ───────────────────────────────────────────────────────────────
  if (!polygonDrawn) {
    return (
      <div className="sidebar-trendline" style={{ position: "relative" }}>
        <div className="sidebar-trendline-title">
          <i className="fa-solid fa-cube" /> 3D Surface Modeler
        </div>
        <div className="sidebar-trendline-content" style={{ position: "relative", padding: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "20px 16px", color: T.muted }}>
            <div style={{ fontSize: 22, flexShrink: 0, color: T.border }}>⬚</div>
            <div style={{ fontSize: 12 }}>Draw a rectangle on the globe to model zone deformation.</div>
          </div>
        </div>
      </div>
    );
  }

  if (!isCesium && (!z.length || z.length < 4)) {
    return (
      <div className="sidebar-trendline" style={{ position: "relative" }}>
        <div className="sidebar-trendline-title">
          <i className="fa-solid fa-cube" /> 3D Surface Modeler
        </div>
        <div className="sidebar-trendline-content" style={{ position: "relative", padding: "16px" }}>
          <div style={{ textAlign: "center", padding: "32px 16px", fontSize: 13, color: T.muted }}>
            {z.length === 0
              ? "No PS points found in that area."
              : `Not enough points (${z.length} found, minimum 4 required). Try a larger area.`}
          </div>
        </div>
      </div>
    );
  }

  // ── Colorscale + aspect ───────────────────────────────────────────────────────
  const xRange  = xGrid.length > 1 ? xGrid[xGrid.length - 1] - xGrid[0] : 1;
  const yRange  = yGrid.length > 1 ? yGrid[yGrid.length - 1] - yGrid[0] : 1;
  const maxRange = Math.max(xRange, yRange);
  const aspectX  = xRange / maxRange;
  const aspectY  = yRange / maxRange;

  // cesium: always use current epoch's actual data range so colour adapts per epoch
  const range = isCesium
    ? Math.max(Math.abs(min), Math.abs(max), 0.001)
    : Math.max(
        Math.abs(rangeMin != null ? rangeMin : min),
        Math.abs(rangeMax != null ? rangeMax : max),
        0.001
      );

  const colorscale = hexToPlotly(effectiveStops);

  const diffRange = Math.max(Math.abs(diffMin), Math.abs(diffMax), 0.001);

  const makeSurfaceTrace = (gridData, cmin, cmax, cs, showscale = false) => ({
    type: "surface",
    x: xGrid, y: yGrid, z: gridData,
    colorscale: cs,
    cmin, cmax,
    showscale,
    colorbar: showscale ? {
      thickness: 12, len: 0.7,
      title: { text: "mm", side: "right", font: { size: 10 } },
      tickfont: { size: 9 },
    } : undefined,
    opacity: 0.8,
    contours: { z: { show: true, usecolormap: true } },
  });

  const surfaceTrace = makeSurfaceTrace(gridZ, -range, range, colorscale, showColourbar && !diffMode);

  const diffColorscale = hexToPlotly(effectiveStops);
  const diffSurfaceTrace = diffGridZ ? makeSurfaceTrace(diffGridZ, -diffRange, diffRange, diffColorscale, showColourbar) : null;

  const scatter3dTrace = {
    type: "scatter3d",
    mode: "markers",
    x, y, z,
    marker: {
      size: 5,
      color: z,
      colorscale,
      cmin: -range,
      cmax: range,
      line: { color: "white", width: 1 },
      opacity: 1,
    },
    hovertemplate: axisMode === "coordinates"
      ? "Lon: %{x:.5f}<br>Lat: %{y:.5f}<br>%{z:.2f} mm<extra>InSAR point</extra>"
      : "E: %{x:.0f} m<br>N: %{y:.0f} m<br>%{z:.2f} mm<extra>InSAR point</extra>",
    showlegend: false,
  };

  const epochIdx = epochs.indexOf(selectedEpoch);

  // ── GIF export ────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    if (!hasEpochs || isExporting) return;
    if (isCesium) {
      const fname = `${(exportFilename.trim() || "surface-deformation")}.gif`;
      setIsExporting(true);
      setGifExportProgress(0);
      setShowExportPanel(false);
      try {
        const blob = await onExportGif?.({
          epochs, filename: exportFilename, title: exportTitle, speed: exportSpeed, dpi: gifExportDpi,
          onProgress: (pct) => setGifExportProgress(pct),
        });
        if (blob) setGifBlobReady({ blob, fname });
      } finally {
        setIsExporting(false);
        setGifExportProgress(null);
      }
      return;
    }
    const Plotly  = window.Plotly;
    const plotDiv = plotDivRef.current;
    if (!plotDiv || !Plotly) return;
    setIsExporting(true);
    setShowExportPanel(false);

    // Use CSS dimensions scaled by DPI factor — Plotly.toImage renders at these sizes
    const scale = gifExportDpi / 96;
    const W = Math.round((plotDiv.clientWidth  || 400) * scale);
    const H = Math.round((plotDiv.clientHeight || 300) * scale);
    const title = exportTitle.trim();
    const fname = (exportFilename.trim() || "surface-deformation") + ".gif";
    const delay = Math.max(50, exportSpeed);

    const gif = new GIF({
      workers: 2, quality: 6, width: W, height: H,
      workerScript: `${window.location.origin}/gif.worker.js`,
    });

    gif.on("finished", blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      setIsExporting(false);
    });

    const loadImg = src => new Promise(res => {
      const img = new Image(); img.onload = () => res(img); img.src = src;
    });

    // Compute IDW grid — keep x/y/z in sync (filter nulls while preserving index mapping)
    const computeGridZ = (epVals) => {
      const xs = [], ys = [], zs = [];
      for (let i = 0; i < epVals.length; i++) {
        if (epVals[i] != null) { xs.push(x[i]); ys.push(y[i]); zs.push(epVals[i]); }
      }
      if (!xs.length) return gridZ;
      const zFlat = new Array(nx * ny);
      let idx = 0;
      for (let j = 0; j < ny; j++) {
        const yv = yGrid[j];
        for (let i2 = 0; i2 < nx; i2++, idx++) {
          zFlat[idx] = idwPredict(xGrid[i2], yv, xs, ys, zs, idwPower);
        }
      }
      const z2d = [];
      for (let j = 0; j < ny; j++) z2d.push(zFlat.slice(j * nx, (j + 1) * nx));
      return z2d;
    };

    for (let ei = 0; ei < epochs.length; ei++) {
      const ep = epochs[ei];
      const epVals = surfaceData.map(p => {
        const v = p.epochs?.[ep];
        return (v != null && isFinite(v)) ? v : null;
      });
      if (!epVals.some(v => v != null)) continue;

      const z2d = computeGridZ(epVals);
      await Plotly.restyle(plotDiv, { z: [z2d] }, [0]);
      await new Promise(r => setTimeout(r, 80)); // let WebGL repaint

      // Plotly.toImage handles WebGL readback correctly (direct canvas.drawImage is always black)
      const imgUrl = await Plotly.toImage(plotDiv, { format: "png", width: W, height: H });
      const img    = await loadImg(imgUrl);

      const offscreen = document.createElement("canvas");
      offscreen.width = W; offscreen.height = H;
      const ctx = offscreen.getContext("2d");
      ctx.drawImage(img, 0, 0);

      // Epoch label — bottom-left
      const epochLabel = fmtEpoch(ep);
      ctx.font = `bold ${Math.round(12 * scale)}px sans-serif`;
      const labelW = ctx.measureText(epochLabel).width + Math.round(12 * scale);
      ctx.fillStyle = "rgba(17,24,39,0.75)";
      ctx.fillRect(Math.round(8 * scale), H - Math.round(30 * scale), labelW, Math.round(22 * scale));
      ctx.fillStyle = "#fff";
      ctx.fillText(epochLabel, Math.round(14 * scale), H - Math.round(14 * scale));

      // Optional title — top-centre
      if (title) {
        ctx.font = `bold ${Math.round(13 * scale)}px sans-serif`;
        const tw = ctx.measureText(title).width + Math.round(16 * scale);
        ctx.fillStyle = "rgba(17,24,39,0.75)";
        ctx.fillRect(Math.round((W - tw) / 2), Math.round(8 * scale), tw, Math.round(22 * scale));
        ctx.fillStyle = "#fff";
        ctx.fillText(title, Math.round((W - tw) / 2) + Math.round(8 * scale), Math.round(24 * scale));
      }

      gif.addFrame(ctx, { copy: true, delay });
      await new Promise(r => setTimeout(r, 0));
    }

    // Restore current gridZ
    if (gridZ) await Plotly.restyle(plotDiv, { z: [gridZ] }, [0]);
    gif.render();
  };

  // ── Diff PNG export ───────────────────────────────────────────────────────────
  const handleDiffExport = async () => {
    if (isDiffExporting || !diffEpochA || !diffEpochB) return;
    if (isCesium) {
      setIsDiffExporting(true);
      setShowDiffExportPanel(false);
      try {
        await onExportDiffPng?.({
          epochA: diffEpochA, epochB: diffEpochB,
          filename: diffExportFilename, title: diffExportTitle,
          showStats: diffExportShowStats, dpi: diffExportDpi,
        });
      } finally {
        setIsDiffExporting(false);
      }
      return;
    }
    const Plotly  = window.Plotly;
    const plotDiv = plotDivRef.current;
    if (!plotDiv || !Plotly) return;
    setIsDiffExporting(true);
    const scale   = diffExportDpi / 96;
    const W       = Math.round((plotDiv.clientWidth  || 400) * scale);
    const H       = Math.round((plotDiv.clientHeight || 300) * scale);
    const TITLE_H = diffExportTitle ? Math.round(28 * scale) : 0;
    const STATS_H = diffExportShowStats ? Math.round(28 * scale) : 0;
    const TOTAL_H = TITLE_H + H + STATS_H;
    const loadImg = src => new Promise(res => { const img = new Image(); img.onload = () => res(img); img.src = src; });
    const imgUrl = await Plotly.toImage(plotDiv, { format: "png", width: W, height: H });
    const img    = await loadImg(imgUrl);
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = TOTAL_H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, TOTAL_H);
    if (diffExportTitle) {
      ctx.fillStyle = T.text;
      ctx.font = `bold ${Math.round(W * 0.032)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText(diffExportTitle, W / 2, TITLE_H - Math.round(8 * scale));
      ctx.textAlign = "left";
    }
    ctx.drawImage(img, 0, TITLE_H);
    if (diffExportShowStats) {
      const statsText = `Avg Δ: ${diffAvg.toFixed(2)} mm  ·  Min Δ: ${diffMin.toFixed(2)} mm  ·  Max Δ: ${diffMax.toFixed(2)} mm  ·  A: ${fmtEpoch(diffEpochA)} → B: ${fmtEpoch(diffEpochB)}`;
      ctx.fillStyle = T.bg;
      ctx.fillRect(0, TITLE_H + H, W, STATS_H);
      ctx.fillStyle = T.muted;
      ctx.font = `${Math.round(10 * scale)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText(statsText, W / 2, TITLE_H + H + Math.round(18 * scale));
      ctx.textAlign = "left";
    }
    canvas.toBlob(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(diffExportFilename || "surface-diff").replace(/\.png$/i, "")}.png`; a.click();
      URL.revokeObjectURL(url);
      setIsDiffExporting(false);
    }, "image/png");
  };

  // ── Colour panel apply handler ────────────────────────────────────────────────
  const handleColourApply = (stops, lock) => {
    setLocalStops(stops);
    setLockToMap(lock);
    setShowColourPanel(false);
    if (lock) onApplyColorStops?.(stops);
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div ref={cardRef} className="sidebar-trendline" style={{ position: "relative" }}>
      <div className="sidebar-trendline-title">
        <i className="fa-solid fa-cube" /> 3D Surface Modeler
      </div>

      <div className="sidebar-trendline-content" style={{ position: "relative", padding: "16px" }}>

        {/* ── Map epoch note (cesium only) ──────────────────────────────── */}
        {isCesium && mapEpoch && (() => {
          const m = mapEpoch.match(/^D_(\d{4})(\d{2})(\d{2})$/i);
          const label = m ? `${m[1]}-${m[2]}-${m[3]}` : mapEpoch;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 10 }}>
              <div style={{
                flex: 1, display: "flex", alignItems: "center", gap: 5,
                fontSize: 10, color: "#444",
                background: "rgba(30,120,255,0.07)",
                border: "1px solid rgba(30,120,255,0.25)",
                borderRadius: 4, padding: "4px 8px",
              }}>
                <span style={{ display: "inline-block", width: 14, height: 0, borderTop: "2px dashed rgba(30,120,255,0.9)", flexShrink: 0 }} />
                <span>Map epoch: <strong>{label}</strong> — surface was created at a different epoch</span>
              </div>
              {onResetEpoch && (
                <button
                  onClick={onResetEpoch}
                  title="Reset map to latest epoch"
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 20, height: 20, padding: 0,
                    border: `1px solid ${T.border}`, borderRadius: 4,
                    background: T.bg, cursor: "pointer", color: T.muted, flexShrink: 0,
                    fontSize: 13, lineHeight: 1, transition: "color 0.15s, border-color 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.borderColor = T.text; }}
                  onMouseLeave={e => { e.currentTarget.style.color = T.muted; e.currentTarget.style.borderColor = T.border; }}
                >✕</button>
              )}
            </div>
          );
        })()}

        {/* ── Section 1: Statistics ─────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {[{ label: "Avg", value: avg }, { label: "Min", value: min }, { label: "Max", value: max }].map(({ label, value }) => (
            <div key={label} style={{ flex: 1, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.subtle, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
                {label}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFeatureSettings: '"tnum"' }}>
                {value.toFixed(2)}
              </div>
              <div style={{ fontSize: 9, color: T.muted }}>mm</div>
            </div>
          ))}
        </div>

        {/* ── Section 2: Surface controls (cesium) / 3D Surface (default) ── */}
        {isCesium ? (
          <SectionBlock>
            {/* IDW interpolation mode */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: T.muted }}>
              <span style={{ whiteSpace: "nowrap", fontWeight: 600, flexShrink: 0 }}>Interpolation</span>
              <div style={{ display: "flex", flex: 1, border: `1px solid ${T.border}`, borderRadius: 5, overflow: "hidden" }}>
                {IDW_MODES.map((m, i) => (
                  <button
                    key={m.label}
                    title={m.title}
                    onClick={() => { setIdwModeIdx(i); onIdwPowerChange?.(m.power); }}
                    style={{
                      flex: 1, padding: "3px 0", fontSize: 10, fontWeight: 600,
                      border: "none",
                      borderLeft: i > 0 ? `1px solid ${T.border}` : "none",
                      background: idwModeIdx === i ? T.text : T.bg,
                      color: idwModeIdx === i ? "#fff" : T.muted,
                      cursor: "pointer", transition: "background 0.12s, color 0.12s",
                    }}
                  >{m.label}</button>
                ))}
              </div>
            </div>
            {/* Surface opacity */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 11, color: T.muted }}>
              <span style={{ whiteSpace: "nowrap", fontWeight: 600, flexShrink: 0 }}>Surface Opacity</span>
              <input
                type="range" min={0} max={1} step={0.01}
                value={opacity}
                onChange={e => { const v = Number(e.target.value); setOpacity(v); onOpacityChange?.(v); }}
                style={{ flex: 1, cursor: "pointer", accentColor: T.text }}
              />
              <span style={{ whiteSpace: "nowrap", fontWeight: 600, color: T.text, width: 30, textAlign: "right" }}>
                {Math.round(opacity * 100)}%
              </span>
            </div>
            {/* Points opacity */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 11, color: T.muted }}>
              <span style={{ whiteSpace: "nowrap", fontWeight: 600, flexShrink: 0 }}>Points Opacity</span>
              <input
                type="range" min={0} max={1} step={0.01}
                value={pointsOpacity}
                onChange={e => { const v = Number(e.target.value); setPointsOpacity(v); onPointsOpacityChange?.(v); }}
                style={{ flex: 1, cursor: "pointer", accentColor: T.text }}
              />
              <span style={{ whiteSpace: "nowrap", fontWeight: 600, color: T.text, width: 30, textAlign: "right" }}>
                {Math.round(pointsOpacity * 100)}%
              </span>
            </div>
          </SectionBlock>
        ) : (
          <>
            <SectionLabel>Deformation Surface</SectionLabel>
            <Plot
              data={[surfaceTrace]}
              layout={{
                margin: { l: 0, r: 0, b: 0, t: 0 },
                scene: {
                  aspectratio: { x: aspectX, y: aspectY, z: 1 / zScale },
                  dragmode: "turntable",
                  camera,
                  xaxis: { title: axisMode === "coordinates" ? "Longitude" : "East–West (m)" },
                  yaxis: { title: axisMode === "coordinates" ? "Latitude"  : "North–South (m)" },
                  zaxis: { title: "Deformation (mm)" },
                },
              }}
              config={{ responsive: true, scrollZoom: true, displaylogo: false, modeBarButtonsToAdd: ["zoom3d","pan3d"] }}
              style={{ width: "100%", height: 300 }}
              onRelayout={evt => { if (evt["scene.camera"]) setCamera(evt["scene.camera"]); }}
            />
            <div style={{ padding: "20px 8px 10px", textAlign: "left" }}>
              <label>
                <strong>Vertical Scale:</strong>{" "}
                <input
                  type="range" min="10" max="20" step="0.2"
                  value={zScale}
                  onChange={e => setZScale(Number(e.target.value))}
                  style={{ margin: "0 15px", verticalAlign: "middle" }}
                />
                <strong>{zScale.toFixed(1)}×</strong>
              </label>
            </div>
            <div style={{ display: "flex", gap: 10, padding: "10px 7px" }}>
              <button
                className={`axis-toggle-btn ${axisMode === "distance" ? "active" : ""}`}
                onClick={() => handleAxisMode("distance")}
                disabled={axisMode === "distance"}
              >Planar Distance</button>
              <button
                className={`axis-toggle-btn ${axisMode === "coordinates" ? "active" : ""}`}
                onClick={() => handleAxisMode("coordinates")}
                disabled={axisMode === "coordinates"}
              >Geographic Coordinates</button>
            </div>
          </>
        )}

        {/* ── Section 3: Historical Data (non-cesium only) ────────────── */}
        {false && isCesium && !hideHistory && (
          <SectionBlock>

            <SectionLabel compact>Historical Data</SectionLabel>

            {epochsLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.muted, padding: "4px 0" }}>
                <Loader2 size={12} color={T.muted} style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
                <span>Querying snapshots…</span>
              </div>
            )}

            {!hasEpochs && !epochsLoading && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 4 }}>
                <label title="Temporal sampling interval across the dataset."
                  style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", cursor: "default" }}>
                  Interval (in months):
                </label>
                <input
                  type="number" min={1} max={60} step={1}
                  value={intervalInput}
                  onChange={e => setIntervalInput(String(Math.round(Number(e.target.value))))}
                  title="Temporal sampling interval across the dataset."
                  style={{
                    width: 40, padding: "3px 6px", fontSize: 12,
                    border: `1px solid ${T.border}`, borderRadius: T.rSm,
                    color: T.text, background: T.bg, outline: "none",
                  }}
                />
                <button
                  onClick={() => onLoadEpochs?.(Math.round(Math.max(1, Number(intervalInput) || 6) * 30.44))}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "5px 10px", background: T.text, color: "#fff",
                    border: "none", borderRadius: T.rSm,
                    fontSize: 11, fontWeight: 600, cursor: "pointer",
                    whiteSpace: "nowrap", transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#374151"}
                  onMouseLeave={e => e.currentTarget.style.background = T.text}
                >
                  <History size={12} /> Load Historical Snapshots
                </button>
              </div>
            )}

            {hasEpochs && !epochsLoading && (
              <>
                {/* ── Interval + Apply + Close — always visible ── */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                  <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", cursor: "default" }}>
                    Interval (months):
                  </label>
                  <input
                    type="number" min={1} max={60} step={1}
                    value={intervalInput}
                    onChange={e => setIntervalInput(String(Math.round(Number(e.target.value))))}
                    style={{ width: 40, padding: "3px 6px", fontSize: 11, border: `1px solid ${T.border}`, borderRadius: T.rSm, color: T.text, background: T.bg, outline: "none" }}
                  />
                  <button
                    onClick={() => { onLoadEpochs?.(Math.round(Math.max(1, Number(intervalInput) || 6) * 30.44)); }}
                    style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: T.rSm, cursor: "pointer", color: T.muted, padding: "3px 7px", fontSize: 11, fontWeight: 600, transition: "color 0.15s, border-color 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.borderColor = T.text; }}
                    onMouseLeave={e => { e.currentTarget.style.color = T.muted; e.currentTarget.style.borderColor = T.border; }}
                  >Apply</button>
                  <div style={{ flex: 1 }} />
                  <button
                    onClick={onCloseEpochs}
                    title="Close historical data"
                    style={{ background: "none", border: "none", cursor: "pointer", color: T.subtle, padding: "2px 4px", fontSize: 14, lineHeight: 1, borderRadius: 4, transition: "color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.color = T.text}
                    onMouseLeave={e => e.currentTarget.style.color = T.subtle}
                  >✕</button>
                </div>

                {/* ── TIME SLIDER accordion ── */}
                <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10, marginTop: 10 }}>
                  <div
                    onClick={() => { if (histMode !== "slider") { setHistMode("slider"); setIsPlaying(false); } }}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: histMode !== "slider" ? "pointer" : "default", marginBottom: histMode === "slider" ? 6 : 0, userSelect: "none", padding: histMode !== "slider" ? "4px 8px" : 0, borderRadius: 4, background: histMode !== "slider" ? T.bg : "transparent", border: histMode !== "slider" ? `1px solid ${T.border}` : "none", transition: "background 0.15s, border-color 0.15s" }}
                    onMouseEnter={e => { if (histMode !== "slider") { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.borderColor = T.muted; } }}
                    onMouseLeave={e => { if (histMode !== "slider") { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = T.border; } }}
                  >
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: histMode === "slider" ? T.text : T.muted, transition: "color 0.15s" }}>
                      {histMode === "slider" ? `Time Slider — ${fmtEpoch(selectedEpoch)}` : "Time Slider"}
                    </span>
                    <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {histMode === "slider" ? (
                        <>
                          <button
                            onClick={handlePlay}
                            disabled={isPlaying}
                            title={isPlaying ? "Playing…" : "Play from first snapshot"}
                            style={{ background: "none", border: "none", padding: "1px 3px", cursor: isPlaying ? "default" : "pointer", color: isPlaying ? T.subtle : T.text, display: "flex", alignItems: "center", opacity: isPlaying ? 0.35 : 1, transition: "opacity 0.15s" }}
                          >
                            <Play size={11} />
                          </button>
                          <button
                            onClick={() => { if (!isExporting && !isPlaying) setShowExportPanel(v => !v); }}
                            disabled={isExporting || isPlaying}
                            title={isExporting ? "Exporting…" : "Export animation as GIF"}
                            style={{ background: "none", border: "none", padding: "1px 3px", cursor: (isExporting || isPlaying) ? "default" : "pointer", color: showExportPanel ? T.text : (isExporting ? T.subtle : T.muted), display: "flex", alignItems: "center", opacity: (isExporting || isPlaying) ? 0.4 : 1, transition: "opacity 0.15s, color 0.15s" }}
                          >
                            {isExporting ? <Loader2 size={11} style={{ animation: "spin 1s linear infinite" }} /> : <Download size={11} />}
                          </button>
                        </>
                      ) : (
                        <span style={{ fontSize: 14, color: T.subtle, lineHeight: 1 }}>›</span>
                      )}
                    </div>
                  </div>

                  {histMode === "slider" && (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.subtle, marginBottom: 4 }}>
                        <span>{fmtEpoch(epochs[0])}</span>
                        <span>{fmtEpoch(epochs[epochs.length - 1])}</span>
                      </div>
                      <input
                        type="range" min={0} max={epochs.length - 1} value={Math.max(0, epochIdx)}
                        onChange={e => { setIsPlaying(false); onEpochChange?.(epochs[Number(e.target.value)]); }}
                        style={{ width: "100%", cursor: "pointer", accentColor: T.text }}
                      />
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                        {epochs.length} snapshots
                        {selectedEpoch !== epochs[epochs.length - 1]
                          ? ` · Δt ${fmtDelta(daysBetween(selectedEpoch, epochs[epochs.length - 1]))} to latest`
                          : " · latest snapshot"}
                      </div>

                      {/* GIF export progress */}
                      {gifExportProgress != null && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 11, color: T.muted }}>
                          <Loader2 size={12} color={T.muted} style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
                          <div style={{ flex: 1, height: 4, borderRadius: 2, background: T.border, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${gifExportProgress}%`, background: T.text, transition: "width 0.3s" }} />
                          </div>
                          <span style={{ whiteSpace: "nowrap", fontWeight: 600, color: T.text }}>{gifExportProgress}%</span>
                        </div>
                      )}

                      {/* GIF blob ready — save button */}
                      {gifBlobReady && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                          <button
                            onClick={() => {
                              const url = URL.createObjectURL(gifBlobReady.blob);
                              const a = document.createElement("a");
                              a.href = url; a.download = gifBlobReady.fname; a.click();
                              URL.revokeObjectURL(url);
                              setGifBlobReady(null);
                            }}
                            style={{
                              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                              padding: "5px 10px", background: "#16a34a", color: "#fff",
                              border: "none", borderRadius: T.rSm,
                              fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "background 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "#15803d"}
                            onMouseLeave={e => e.currentTarget.style.background = "#16a34a"}
                          >
                            <Download size={12} /> Save GIF
                          </button>
                          <button
                            onClick={() => setGifBlobReady(null)}
                            style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: T.rSm, padding: "5px 8px", cursor: "pointer", color: T.muted, fontSize: 11, transition: "color 0.15s, border-color 0.15s" }}
                            onMouseEnter={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.borderColor = T.text; }}
                            onMouseLeave={e => { e.currentTarget.style.color = T.muted; e.currentTarget.style.borderColor = T.border; }}
                          >✕</button>
                        </div>
                      )}

                      {/* Export options panel */}
                      {showExportPanel && (
                        <div style={{
                          marginTop: 10, padding: "10px 12px",
                          border: `1px solid ${T.border}`, borderRadius: T.rSm,
                          background: T.bg,
                        }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.muted, marginBottom: 8 }}>
                            GIF Export Options
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <label style={{ fontSize: 11, color: T.muted, width: 60, flexShrink: 0 }}>Filename</label>
                              <input
                                value={exportFilename}
                                onChange={e => setExportFilename(e.target.value)}
                                placeholder="surface-deformation"
                                style={{ flex: 1, padding: "3px 6px", fontSize: 11, border: `1px solid ${T.border}`, borderRadius: T.rSm, color: T.text, background: "#fff", outline: "none" }}
                              />
                              <span style={{ fontSize: 11, color: T.muted, flexShrink: 0 }}>.gif</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <label style={{ fontSize: 11, color: T.muted, width: 60, flexShrink: 0 }}>Title</label>
                              <input
                                value={exportTitle}
                                onChange={e => setExportTitle(e.target.value)}
                                placeholder="Optional — shown top-centre"
                                style={{ flex: 1, padding: "3px 6px", fontSize: 11, border: `1px solid ${T.border}`, borderRadius: T.rSm, color: T.text, background: "#fff", outline: "none" }}
                              />
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <label style={{ fontSize: 11, color: T.muted, width: 60, flexShrink: 0 }}>Speed</label>
                              <input
                                type="range" min={50} max={1000} step={50}
                                value={exportSpeed}
                                onChange={e => setExportSpeed(Number(e.target.value))}
                                style={{ flex: 1, cursor: "pointer", accentColor: T.text }}
                              />
                              <span style={{ fontSize: 11, color: T.text, fontWeight: 600, width: 50, textAlign: "right", flexShrink: 0 }}>
                                {exportSpeed}ms
                              </span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <label style={{ fontSize: 11, color: T.muted, width: 60, flexShrink: 0 }}>Quality</label>
                              <div style={{ display: "flex", flex: 1, border: `1px solid ${T.border}`, borderRadius: 4, overflow: "hidden" }}>
                                {[96, 150, 300].map((dpi, i) => (
                                  <button key={dpi} onClick={() => setGifExportDpi(dpi)} style={{ flex: 1, padding: "3px 0", fontSize: 10, fontWeight: 600, border: "none", borderLeft: i > 0 ? `1px solid ${T.border}` : "none", background: gifExportDpi === dpi ? T.text : T.bg, color: gifExportDpi === dpi ? "#fff" : T.muted, cursor: "pointer", transition: "background 0.12s, color 0.12s" }}>
                                    {dpi === 96 ? "Screen" : `${dpi} DPI`}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={handleExport}
                            style={{ marginTop: 10, width: "100%", padding: "5px 0", background: T.text, color: "#fff", border: "none", borderRadius: T.rSm, fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "background 0.12s" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#374151"}
                            onMouseLeave={e => e.currentTarget.style.background = T.text}
                          >
                            Export GIF
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* ── DIFFERENCE SURFACE accordion ── */}
                <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 8 }}>
                  <div
                    onClick={() => {
                      if (histMode !== "diff") {
                        setHistMode("diff");
                        setIsPlaying(false);
                        setDiffEpochA(epochs[0] || null);
                        setDiffEpochB(epochs[epochs.length - 1] || null);
                      }
                    }}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: histMode !== "diff" ? "pointer" : "default", marginBottom: histMode === "diff" ? 6 : 0, userSelect: "none", padding: histMode !== "diff" ? "4px 8px" : 0, borderRadius: 4, background: histMode !== "diff" ? T.bg : "transparent", border: histMode !== "diff" ? `1px solid ${T.border}` : "none", transition: "background 0.15s, border-color 0.15s" }}
                    onMouseEnter={e => { if (histMode !== "diff") { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.borderColor = T.muted; } }}
                    onMouseLeave={e => { if (histMode !== "diff") { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = T.border; } }}
                  >
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: histMode === "diff" ? T.text : T.muted, transition: "color 0.15s" }}>
                      Difference Surface
                    </span>
                    <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {histMode === "diff" ? (
                        <button
                          onClick={() => { if (!isDiffExporting) setShowDiffExportPanel(v => !v); }}
                          title={isDiffExporting ? "Exporting…" : "Export as PNG"}
                          style={{ background: "none", border: "none", padding: "1px 3px", cursor: isDiffExporting ? "default" : "pointer", color: showDiffExportPanel ? T.text : T.muted, display: "flex", alignItems: "center", opacity: isDiffExporting ? 0.4 : 1, transition: "opacity 0.15s, color 0.15s" }}
                        >
                          {isDiffExporting ? <Loader2 size={11} style={{ animation: "spin 1s linear infinite" }} /> : <Download size={11} />}
                        </button>
                      ) : (
                        <span style={{ fontSize: 14, color: T.subtle, lineHeight: 1 }}>›</span>
                      )}
                    </div>
                  </div>

                  {histMode === "diff" && (
                    <>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 9, color: T.subtle, marginBottom: 2 }}>Epoch A (from)</div>
                          <select
                            value={diffEpochA || ""}
                            onChange={e => {
                              const val = e.target.value || null;
                              setDiffEpochA(val);
                              if (val && diffEpochB && diffEpochB <= val) {
                                const nextIdx = epochs.indexOf(val) + 1;
                                setDiffEpochB(epochs[nextIdx] || null);
                              }
                            }}
                            style={{ width: "100%", fontSize: 10, padding: "3px 4px", border: `1px solid ${T.border}`, borderRadius: 4, background: "#fff", color: T.text, outline: "none" }}
                          >
                            <option value="">— select —</option>
                            {epochs.slice(0, -1).map(ep => (
                              <option key={ep} value={ep}>{fmtEpoch(ep)}</option>
                            ))}
                          </select>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 9, color: T.subtle, marginBottom: 2 }}>Epoch B (to)</div>
                          <select
                            value={diffEpochB || ""}
                            onChange={e => setDiffEpochB(e.target.value || null)}
                            style={{ width: "100%", fontSize: 10, padding: "3px 4px", border: `1px solid ${T.border}`, borderRadius: 4, background: "#fff", color: T.text, outline: "none" }}
                          >
                            <option value="">— select —</option>
                            {epochs.filter(ep => !diffEpochA || ep > diffEpochA).map(ep => (
                              <option key={ep} value={ep}>{fmtEpoch(ep)}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {diffEpochA && diffEpochB && diffGridZ && (
                        <>
                          <div style={{ marginTop: 6, fontSize: 10, color: T.muted, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 4px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: T.rSm, padding: "6px 10px" }}>
                            <span style={{ color: T.subtle, fontSize: 9, fontWeight: 600 }}>Avg Δ</span>
                            <span style={{ color: T.subtle, fontSize: 9, fontWeight: 600 }}>Min Δ</span>
                            <span style={{ color: T.subtle, fontSize: 9, fontWeight: 600 }}>Max Δ</span>
                            <span style={{ fontWeight: 600, color: T.text }}>{diffAvg.toFixed(2)} mm</span>
                            <span style={{ fontWeight: 600, color: T.text }}>{diffMin.toFixed(2)} mm</span>
                            <span style={{ fontWeight: 600, color: T.text }}>{diffMax.toFixed(2)} mm</span>
                          </div>
                          {showDiffExportPanel && (
                            <div style={{ marginTop: 10, padding: "10px 12px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: T.rSm, display: "flex", flexDirection: "column", gap: 8 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.muted, marginBottom: 2 }}>PNG Export</div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", minWidth: 60 }}>Filename</label>
                                <input type="text" value={diffExportFilename} onChange={e => setDiffExportFilename(e.target.value)} placeholder="surface-diff"
                                  style={{ flex: 1, padding: "3px 6px", fontSize: 11, border: `1px solid ${T.border}`, borderRadius: T.rSm, color: T.text, background: "#fff", outline: "none" }} />
                                <span style={{ fontSize: 11, color: T.subtle }}>.png</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", minWidth: 60 }}>Title</label>
                                <input type="text" value={diffExportTitle} onChange={e => setDiffExportTitle(e.target.value)} placeholder="optional title"
                                  style={{ flex: 1, padding: "3px 6px", fontSize: 11, border: `1px solid ${T.border}`, borderRadius: T.rSm, color: T.text, background: "#fff", outline: "none" }} />
                              </div>
                              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.muted, cursor: "pointer" }}>
                                <input type="checkbox" checked={diffExportShowStats} onChange={e => setDiffExportShowStats(e.target.checked)} />
                                Include Δ statistics footer
                              </label>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", minWidth: 60 }}>Quality</label>
                                <div style={{ display: "flex", flex: 1, border: `1px solid ${T.border}`, borderRadius: 4, overflow: "hidden" }}>
                                  {[96, 150, 300].map((dpi, i) => (
                                    <button key={dpi} onClick={() => setDiffExportDpi(dpi)} style={{ flex: 1, padding: "3px 0", fontSize: 10, fontWeight: 600, border: "none", borderLeft: i > 0 ? `1px solid ${T.border}` : "none", background: diffExportDpi === dpi ? T.text : T.bg, color: diffExportDpi === dpi ? "#fff" : T.muted, cursor: "pointer", transition: "background 0.12s, color 0.12s" }}>
                                      {dpi === 96 ? "Screen" : `${dpi} DPI`}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <button
                                onClick={handleDiffExport} disabled={isDiffExporting}
                                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "5px 10px", background: T.text, color: "#fff", border: "none", borderRadius: T.rSm, fontSize: 11, fontWeight: 600, cursor: isDiffExporting ? "default" : "pointer", opacity: isDiffExporting ? 0.5 : 1, transition: "background 0.15s" }}
                                onMouseEnter={e => { if (!isDiffExporting) e.currentTarget.style.background = "#374151"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = T.text; }}
                              >
                                {isDiffExporting ? <><Loader2 size={11} style={{ animation: "spin 1s linear infinite" }} /> Exporting…</> : <><Download size={11} /> Export PNG</>}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

          </SectionBlock>
        )}

      </div>
    </div>
  );
}
